import { customElement, property } from 'lit/decorators.js';
import { localized, msg, str } from '@lit/localize';
import { LitElement, html, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import { styleMap } from 'lit/directives/style-map.js';

import "../navigation/usos-link";

import style from './usos-frame-section.scss';

/**
 * Section with title to be used as usos-frame's child
 *
 * @slot - frame content
 */
@customElement('usos-frame-section')
@localized()
export class UsosFrameSection extends LitElement {
    static override styles = style;


    /**
     * Title of the section.
     */
    @property({attribute: 'section-title'}) sectionTitle = '';

    /**
     * Leading icon name, default 'outlined.label', {@see UsosIcon.iconName}.
     */
    @property({attribute: 'leading-icon'}) leadingIcon: string | null = null;

    /**
     * Should the component be expadable.
     */
    @property({type: Boolean, reflect: true}) expandable = false;

    /**
     * If usos-frame is expandable, then this attribute indicates whether it is expanded or not.
     */
    @property({type: Boolean, reflect: true}) expanded = false;

    /**
     * Should behave as subgrid
     */
    @property({type: Boolean, reflect: true}) subgrid = false;

    @property({type: Number}) level: Optional<number> = null;


    override render() {
        const contentStyle = { display: this.expandable && !this.expanded ? 'none' : null };
        return html`
            <div>
                ${when(this.expandable, () => this._renderExpandableTitle(), () => this._renderNonExpandableTitle())}
                <div id="content" style=${styleMap(contentStyle)} role="region" aria-labelledby="title">
                    <slot></slot>
                </div>
            </div>
        `
    }

    private _renderExpandableTitle() {
        return html`
            <button id="title" @click=${this._onExpandCollapseClick} aria-controls="content" aria-expanded=${this.expanded ? "true" : "false"}>
                <usos-icon icon-name="${this.leadingIcon ?? 'outlined.label'}"></usos-icon>
                <div role="heading" aria-level=${this.level ? this.level + 1 : nothing}>${this.sectionTitle}</div>
                <usos-icon id="expand-icon" .iconName=${this.expanded ? 'expand_less' : 'expand_more'}
                    .label=${this.expanded ? msg(str`Zwiń`) : msg(str`Rozwiń`)}
                ></usos-icon>
            </button>
        `;
    }

    private _renderNonExpandableTitle() {
        return html`
            <div id="title">
                <usos-icon icon-name="${this.leadingIcon ?? 'outlined.label'}"></usos-icon>
                <div role="heading" aria-level=${this.level ? this.level + 1 : nothing}>${this.sectionTitle}</div>
            </div>
        `;
    }

    private _onExpandCollapseClick(e: Event) {
        e.stopImmediatePropagation();
        this.expanded = !this.expanded;
    }
}

declare global {
    interface HTMLElementTagNameMap {
      "usos-frame-section": UsosFrameSection,
    }
}
